import { useEffect, useState } from "react";
import { loadConflicts, loadMeta, loadReplays, loadStats } from "./lib/data";
import { startReplay, stepChapter, togglePlay } from "./lib/replayEngine";
import { useApp } from "./lib/store";
import { formatUrl, parseUrl } from "./lib/url";
import { MapView } from "./map/MapView";
import { About } from "./panels/About";
import { Controls, Legend, Tooltip } from "./panels/Controls";
import { motionFx, useFx } from "./fx/state";
import { Letterbox, ReplayEnd, ReplayIntro, Welcome } from "./panels/Cinema";
import { ReplayOverlay, ReplayPicker } from "./panels/Replays";
import { SidePanel } from "./panels/SidePanel";
import { TopBar } from "./panels/TopBar";
import { Timeline } from "./timeline/Timeline";

const INITIAL_SPAN = 260;

export function App() {
  const error = useApp((s) => s.error);
  const ready = useApp((s) => !!s.conflicts);
  const replaying = useApp((s) => !!s.replay);
  const replayList = useApp((s) => s.panel === "replays");
  const cinematic = useFx((s) => motionFx(s));
  const [hint, setHint] = useState(false);
  const touch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  // bootstrap: data, then state from the URL
  useEffect(() => {
    (async () => {
      try {
        const meta = await loadMeta();
        const [conflicts, stats] = await Promise.all([loadConflicts(), loadStats()]);
        const u = parseUrl(window.location.search);
        const st = useApp.getState();
        st.setData(meta, conflicts, stats);
        st.setSpan(u.s ?? INITIAL_SPAN);
        st.setT(u.t ?? meta.now);
        if (u.m) st.setView(u.m);
        if (u.c && conflicts.byId.has(u.c)) st.select(conflicts.byId.get(u.c)!, { fly: !u.m });
        if (u.m) st.fly({ lon: u.m.lon, lat: u.m.lat, zoom: u.m.zoom });
        if (window.matchMedia("(max-width: 900px)").matches && !u.c) st.setSheet("collapsed");
        try { setHint(!localStorage.getItem("hint-seen") && !u.r); } catch { setHint(!u.r); }
        loadReplays(meta.replays).then(({ replays }) => {
          st.setReplays(replays);
          if (!u.r) return;
          const def = replays.find((r) => r.id === u.r);
          const i = conflicts.byId.get(u.r!);
          if (def || i != null) startReplay(def ? { def } : { i }, { leadIn: 2.5 });
        }).catch(() => st.setReplays([]));
      } catch (e) {
        useApp.getState().setError(`The data could not be loaded (${e instanceof Error ? e.message : e}). Please reload.`);
      }
    })();
  }, []);

  // URL sync (debounced, replaces history so the back button is not flooded)
  useEffect(() => {
    let timer: number | undefined;
    return useApp.subscribe((s, p) => {
      if (!s.conflicts || (s.t === p.t && s.span === p.span && s.selected === p.selected && s.view === p.view && !!s.replay === !!p.replay)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const st = useApp.getState();
        const url = st.replay ? formatUrl({ r: st.replay.key }) :
          formatUrl({ t: st.t, s: st.span, c: st.selected >= 0 ? st.conflicts!.id[st.selected] : undefined, m: st.view });
        window.history.replaceState(null, "", `${window.location.pathname}${url}`);
      }, 300);
    });
  }, []);

  // a replay ends when the playhead is taken well outside its war (an era, "Today", a search result)
  useEffect(() => useApp.subscribe((s) => {
    const r = s.replay;
    if (r && (s.t < r.t0 - (r.t1 - r.t0) * 0.1 - 1 / 365 || s.t > r.t1 + (r.t1 - r.t0) * 0.1 + 1 / 365)) s.endReplay();
  }), []);

  // global keys: Escape closes the event, the replay, the replay list, the detail; space plays; arrows step chapters
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName ?? "").toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea") return;
      const st = useApp.getState();
      if (e.key === "Escape") {
        if (st.panel === "replays") st.setPanel("moment"); // the war picker is on top of everything else
        else if (st.event) st.setEvent(null);
        else if (st.replay) st.endReplay();
        else if (st.selected >= 0) st.select(-1);
      }
      if (st.panel === "replays") return; // the picker is modal
      if (e.key === " " && !(document.activeElement instanceof HTMLButtonElement) && tag !== "canvas") { e.preventDefault(); togglePlay(); }
      if (st.replay && tag !== "canvas" && (e.key === "ArrowRight" || e.key === "ArrowLeft") && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        stepChapter(e.key === "ArrowRight" ? 1 : -1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const dismissHint = () => {
    setHint(false);
    try { localStorage.setItem("hint-seen", "1"); } catch { /* private mode */ }
  };

  return (
    <div className="app">
      <TopBar />
      <main className="stage">
        <div className={`map-wrap${replaying ? " replaying" : ""}${replaying && cinematic ? " cinematic" : ""}`} inert={replayList}>
          <MapView />
          <Letterbox />
          <Legend />
          <Tooltip />
          <ReplayIntro />
          <ReplayOverlay />
          <ReplayEnd />
          {!ready && !error && <div className="loading-overlay" role="status">Loading the atlas…</div>}
          {error && <div className="error-overlay" role="alert">{error}</div>}
          {hint && ready && !replaying && !replayList && <Welcome touch={touch} onClose={dismissHint} />}
        </div>
        <SidePanel />
        {replayList && <ReplayPicker />}
      </main>
      <footer className="dock" inert={replayList}>
        <Controls />
        <Timeline />
      </footer>
      <About />
    </div>
  );
}
